import { useCallback, useEffect, useRef, useState } from 'react'
import { useVoiceStore } from '../../stores/voiceStore'
import { useAuthStore } from '../../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { channelApi } from '../../services/api'
import TranscriptionPanel from './TranscriptionPanel'
import TelegramMessageList from '@os/messenger/web/components/telegram/TelegramMessageList'
import TelegramMessageInput, { type TelegramMessageInputHandle } from '@os/messenger/web/components/telegram/TelegramMessageInput'
import ChannelDropZone from '../chat/ChannelDropZone'
import type { Message } from '../../types'
import { Hash, FileText, Activity, ChevronDown, ChevronUp } from 'lucide-react'
import ActivityLogPanel from './ActivityLogPanel'
import { getSocket } from '../../services/socket'

interface Props {
  channelId: string
}

/**
 * Right-side panel for voice channels. Hosts two tabs:
 *   - Transcription (live STT bubbles, session-scoped — empties when
 *     the room empties, mirrors the existing TranscriptionPanel)
 *   - Chat (Telegram-style text chat backed by the same channelId,
 *     filtered server-side to chatSessionStartedAt so each fresh
 *     occupancy reads as an empty chat while DB rows persist).
 *
 * Tab choice persists via voiceStore.sidePanelTab (localStorage), so a
 * user who prefers Chat keeps seeing it on every join.
 */
export default function VoiceChannelSidePanel({ channelId }: Props) {
  const sidePanelTab = useVoiceStore((s) => s.sidePanelTab)
  const setSidePanelTab = useVoiceStore((s) => s.setSidePanelTab)
  // OSPROD-52: badge count for incoming chat messages received while
  // the Chat tab wasn't visible. setSidePanelTab('chat') resets it
  // atomically — see voiceStore.
  const sidePanelChatUnread = useVoiceStore((s) => s.sidePanelChatUnread)
  // OSPROD-73: collapse state for the whole strip + the toggle. While
  // collapsed only the tab bar shows; the message list / transcript
  // body is hidden so the voice grid above can claim that space.
  const isCollapsed = useVoiceStore((s) => s.isSidePanelCollapsed)
  const toggleCollapsed = useVoiceStore((s) => s.toggleSidePanelCollapsed)

  const currentUser = useAuthStore((s) => s.user)
  const servers = useServerStore((s) => s.servers)
  // Resolve the channel out of the active server tree so we can read
  // adminOnlyWrite + the user's owner/admin verdict without an extra
  // round-trip. Same lookup pattern chat/MessageInput uses.
  const channelInfo = (() => {
    if (!channelId) return null
    for (const s of servers) {
      const direct = s.channels?.find((c) => c.id === channelId)
      if (direct) return { channel: direct, server: s }
      for (const cat of s.categories || []) {
        const inCat = cat.channels?.find((c) => c.id === channelId)
        if (inCat) return { channel: inCat, server: s }
      }
    }
    return null
  })()
  const isAdminOnly = !!channelInfo?.channel.adminOnlyWrite
  const isOwnerOrAdmin = !!currentUser?.isAdmin || channelInfo?.server.ownerId === currentUser?.id
  const canWrite = !isAdminOnly || isOwnerOrAdmin

  const [isHost, setIsHost] = useState(false)
  const [tagVocabulary, setTagVocabulary] = useState<{ id: string; label: string; color: string | null }[]>([])

  useEffect(() => {
    if (!channelId) return
    channelApi.hostStatus(channelId).then((r: any) => setIsHost(!!r.data.isHost)).catch(() => setIsHost(false))
    channelApi.listMessageTags(channelId).then((r: any) => setTagVocabulary(r.data)).catch(() => setTagVocabulary([]))
  }, [channelId])

  useEffect(() => {
    if (!channelId || sidePanelTab !== 'activity') return
    try { getSocket().emit('voice:getActivity', { channelId }) } catch { /* socket not ready */ }
  }, [channelId, sidePanelTab])

  const handleTag = useCallback((messageId: string, tagDefId: string) => {
    channelApi.tagMessage(channelId, messageId, tagDefId).catch((e) => console.error('[voice-chat] tag failed', e))
  }, [channelId])
  const handleUntag = useCallback((messageId: string, tagId: string) => {
    channelApi.untagMessage(channelId, messageId, tagId).catch((e) => console.error('[voice-chat] untag failed', e))
  }, [channelId])
  const handlePin = useCallback((messageId: string, pinned: boolean) => {
    channelApi.pinMessage(channelId, messageId, pinned).catch((e) => console.error('[voice-chat] pin failed', e))
  }, [channelId])

  // Reply state lives in this panel — the chat lives in a side strip
  // not a route, so we don't have a parent route component to host it.
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null)
  // OSPROD-75: ref to the composer so the drop zone can hand dropped
  // files straight into the preview+caption modal instead of triggering
  // a silent background upload.
  const inputRef = useRef<TelegramMessageInputHandle>(null)

  // OSPROD-73: when the panel is collapsed, rendering the body would
  // mount socket listeners + message lists for nothing — and the
  // OSPROD-69 jump-to-bottom pill would freeze pointing at a viewport
  // the user can't see. Skip the body entirely; the badge logic in
  // voiceStore guarantees the unread count keeps incrementing while
  // the body is unmounted.
  return (
    <div className="flex flex-col h-full bg-card rounded-xl ring-1 ring-border shadow-md overflow-hidden">
      {/* Tab bar — same height + colour treatment as the existing
          transcription header so swapping tabs doesn't make the panel
          jump. */}
      <div className="px-2 pt-1.5 pb-1 border-b border-border flex items-center gap-1 shrink-0">
        <TabButton
          active={sidePanelTab === 'chat' && !isCollapsed}
          onClick={() => {
            // OSPROD-73: clicking Chat from the collapsed state both
            // selects the tab AND opens the panel — otherwise the
            // user would have to click twice (tab, then chevron) to
            // actually read the messages they came for.
            if (isCollapsed && sidePanelTab === 'chat') {
              toggleCollapsed()
            } else {
              setSidePanelTab('chat')
              if (isCollapsed) toggleCollapsed()
            }
          }}
          icon={<Hash size={12} />}
          label="Chat"
          badgeCount={sidePanelChatUnread}
        />
        <TabButton
          active={sidePanelTab === 'transcript' && !isCollapsed}
          onClick={() => {
            if (isCollapsed && sidePanelTab === 'transcript') {
              toggleCollapsed()
            } else {
              setSidePanelTab('transcript')
              if (isCollapsed) toggleCollapsed()
            }
          }}
          icon={<FileText size={12} />}
          label="Transcript"
        />
        <TabButton
          active={sidePanelTab === 'activity' && !isCollapsed}
          onClick={() => {
            if (isCollapsed && sidePanelTab === 'activity') {
              toggleCollapsed()
            } else {
              setSidePanelTab('activity')
              if (isCollapsed) toggleCollapsed()
            }
          }}
          icon={<Activity size={12} />}
          label="Activity"
        />
        <button
          type="button"
          onClick={toggleCollapsed}
          className="ml-auto p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition"
          // Word "chat" intentionally left out — the panel hosts both
          // chat AND transcript, and OSPROD-52's existing test looks
          // up the Chat tab via getByRole(button, name=/chat/i). If
          // this button also said "chat" it would match the same
          // regex and break the test.
          aria-label={isCollapsed ? 'Expand panel' : 'Collapse panel'}
          title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {isCollapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>
      {!isCollapsed && (
        <div className="flex-1 min-h-0 overflow-hidden">
          {sidePanelTab === 'transcript' ? (
            // Re-host TranscriptionPanel without its own header — the
            // tab bar above replaces it. Simplest way: render the panel
            // and let it draw its own header; cosmetic duplication
            // (header inside header) is acceptable until designer pass.
            <TranscriptionPanel />
          ) : sidePanelTab === 'activity' ? (
            <ActivityLogPanel />
          ) : (
            // OSPROD-75: drag-and-drop hands files to the composer's
            // preview+caption modal (same as the paperclip path) instead
            // of running a silent inline upload — so the dropped image
            // gets a thumbnail, a caption field, and a Send button.
            // OSPROD-80: min-h-0 on this intermediate flex column too —
            // any link in the chain without it lets the input's natural
            // height cascade up and overflow the 288 px side panel.
            <div className="flex flex-col h-full min-h-0 bg-background">
              <ChannelDropZone onFilesDropped={(files) => inputRef.current?.openWithFiles(files)}>
                <TelegramMessageList
                  target={{ kind: 'channel', id: channelId, canWrite, channelType: 'VOICE' }}
                  onReply={(m) => setReplyToMessage(m)}
                  canTag={isHost}
                  tagVocabulary={tagVocabulary}
                  onTag={handleTag}
                  onUntag={handleUntag}
                  onPin={handlePin}
                />
                <TelegramMessageInput
                  ref={inputRef}
                  target={{ kind: 'channel', id: channelId, canWrite, channelType: 'VOICE' }}
                  replyToId={replyToMessage?.id || null}
                  replyToMessage={replyToMessage || undefined}
                  onCancelReply={() => setReplyToMessage(null)}
                  // OSPROD-80: the side panel is only 288 px tall, so the
                  // default 200 px textarea max-h leaves zero room for
                  // the rest of the input row. `compact` shrinks the cap
                  // to fit; full-screen DMs/text channels keep the wide
                  // cap.
                  compact
                />
              </ChannelDropZone>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
  badgeCount = 0,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  /** OSPROD-52: optional unread count next to the label. Hidden when
   *  zero or when the tab is already active (the badge represents
   *  "messages you haven't seen yet"; if the tab IS open, you're
   *  seeing them now so it must not pop up briefly before clearing). */
  badgeCount?: number
}) {
  const showBadge = !active && badgeCount > 0
  const badgeText = badgeCount > 99 ? '99+' : String(badgeCount)
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 transition ${
        active
          ? 'bg-primary/15 text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
      }`}
    >
      {icon}
      {label}
      {showBadge && (
        <span
          className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none normal-case tracking-normal"
          aria-label={`${badgeCount} new ${badgeCount === 1 ? 'message' : 'messages'}`}
        >
          {badgeText}
        </span>
      )}
    </button>
  )
}
